/**
 * DA-54: RatingInput Component
 *
 * Interactive star rating input (1-5 stars).
 * Provides hover preview and click selection.
 */

import React from 'react';
import { Star } from 'lucide-react';
import { cn } from '@/lib/utils';

interface RatingInputProps {
  value: number; // Current rating (0-5)
  onChange: (rating: number) => void;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const RatingInput: React.FC<RatingInputProps> = ({ value, onChange, disabled = false, size = 'md', className }) => {
  const [hoverValue, setHoverValue] = React.useState<number | null>(null);

  const starSizeClass = {
    sm: 'size-4',
    md: 'size-6',
    lg: 'size-8',
  }[size];

  const displayValue = hoverValue !== null ? hoverValue : value;

  return (
    <div className={cn('flex items-center gap-1', className)}>
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type='button'
          disabled={disabled}
          onClick={() => onChange(star)}
          onMouseEnter={() => !disabled && setHoverValue(star)}
          onMouseLeave={() => setHoverValue(null)}
          className={cn('transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded', disabled && 'cursor-not-allowed opacity-50')}
          aria-label={`${star} Stern${star !== 1 ? 'e' : ''}`}
        >
          <Star className={cn(starSizeClass, star <= displayValue ? 'text-warning fill-warning' : 'text-muted-foreground/30 fill-muted-foreground/30')} />
        </button>
      ))}
    </div>
  );
};
